import type { ParagraphProps } from './Paragraph'
import Paragraph from './Paragraph'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import type { HTMLAttributes, ReactNode } from 'react'
import { isValidElement } from 'react'
import { isEmpty } from 'lodash'
import type { IconProps } from './Icon'
import Icon from './Icon'
import { Info } from 'lucide-react'

const iconStyle = cva(
    'h-[24px] w-[24px] min-h-[24px] min-w-[24px] rounded-2 flex items-center justify-center border-0.5',
    {
        variants: {
            intent: {
                default: 'bg-backgroundNeutralStrong border-borderPrimary',
                information: 'bg-backgroundInformationStrong border-borderInformation',
                warning: 'bg-backgroundWarningStrong border-borderWarning',
                negative: 'bg-backgroundNegativeStrong border-borderNegative',
                positive: 'bg-backgroundPositiveStrong border-borderPositive',
                negativeSecondary: 'bg-backgroundNegativeLight border-borderNegative',
                warningSecondary: 'bg-backgroundWarningLight border-borderWarning'
            }
        },
        defaultVariants: {
            intent: 'default'
        }
    }
)

const bannerStyle = cva('rounded-3 w-full p-5 border-0.5 shadow-1', {
    variants: {
        intent: {
            default: 'bg-backgroundNeutralLight border-borderPrimary',
            information: 'bg-backgroundInformationLight border-borderInformation',
            warning: 'bg-backgroundWarningLight border-borderWarning',
            negative: 'bg-backgroundNegativeLight border-borderNegative',
            positive: 'bg-backgroundPositiveLight border-borderPositive',
            negativeSecondary: 'bg-backgroundPrimary border-borderPrimary',
            warningSecondary: 'bg-backgroundPrimary border-borderPrimary'
        }
    },
    defaultVariants: {
        intent: 'default'
    }
})

const titleStyle = cva('flex gap-x-1 gap-y-2 justify-center h-full text-left min-w-0', {
    variants: {
        intent: {
            default: 'text-textPrimary',
            information: 'text-textPrimary',
            warning: 'text-textPrimary',
            negative: 'text-textPrimary',
            positive: 'text-textPrimary',
            negativeSecondary: 'text-textNegativeSecondary',
            warningSecondary: 'text-textWarning'
        },
        alignment: {
            column: 'flex-col',
            row: 'flex-row  items-center'
        }
    },
    defaultVariants: {
        alignment: 'column',
        intent: 'default'
    }
})

export type BannerProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> &
    VariantProps<typeof bannerStyle> & {
        title: ReactNode
        description?: ReactNode
        callToAction?: ReactNode
        icon?: IconProps['icon']
        dataTestId?: string
    } & VariantProps<typeof titleStyle>

const Banner = ({
    title,
    description,
    className,
    intent,
    callToAction,
    icon,
    alignment,
    dataTestId,
    ...props
}: BannerProps) => {
    const intentToColor = (): { icon: IconProps['color']; text?: ParagraphProps['color'] } => {
        switch (intent) {
            case 'negativeSecondary':
                return { icon: 'negativeSecondary', text: 'negativeSecondary' }
            case 'warningSecondary':
                return { icon: 'warning', text: 'warning' }
            case undefined:
            case null:
            case 'default':
            case 'information':
            case 'negative':
            case 'positive':
            case 'warning':
            default:
                return { icon: 'onColor' }
        }
    }
    const renderIcon = icon ?? Info
    const renderDescription = (): ReactNode => {
        if (!isEmpty(description)) {
            return isValidElement(description) ? description : <Paragraph>{description}</Paragraph>
        }
        return null
    }

    return (
        <div
            className={bannerStyle({
                intent,
                className
            })}
            data-testid={dataTestId ?? '__banner__'}
            {...props}
        >
            <div
                className={`flex ${callToAction ? 'justify-between' : 'justify-start'} ${callToAction ? 'gap-5' : ''}`}
            >
                <div className="flex gap-5 items-center min-w-0">
                    <div
                        className={iconStyle({
                            intent
                        })}
                    >
                        <Icon
                            data-testid={renderIcon.displayName}
                            icon={renderIcon}
                            size="1"
                            color={intentToColor().icon}
                        />
                    </div>
                    <div
                        className={titleStyle({
                            alignment
                        })}
                    >
                        <Paragraph variant="medium" color={intentToColor().text}>
                            {title}
                        </Paragraph>
                        {renderDescription()}
                    </div>
                </div>
                {callToAction ? (
                    <div data-testid="__banner-cta__" className="min-w-fit">
                        {callToAction}
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default Banner
